import { type ButtonHTMLAttributes, type Ref } from 'react'
import { cn } from '../utils/cn'

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?:
    | 'primary'
    | 'secondary'
    | 'ghost'
    | 'danger'
    | 'icon'
    | 'warning'
    | 'info'
  size?: 'sm' | 'md' | 'lg' | 'icon-sm' | 'icon-md' | 'icon-lg'
  fullWidth?: boolean
  loading?: boolean
  leftIcon?: React.ReactNode
  rightIcon?: React.ReactNode
  ref?: Ref<HTMLButtonElement>
}

function Button({
  className,
  variant = 'primary',
  size = 'md',
  fullWidth = false,
  loading = false,
  disabled,
  leftIcon,
  rightIcon,
  children,
  ref,
  ...props
}: ButtonProps) {
  const baseStyles =
    'inline-flex items-center justify-center font-medium transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed'

  const variants = {
    primary:
      'bg-morandi-sage-500 text-white hover:bg-morandi-sage-400 focus:ring-morandi-sage-400 shadow-md hover:shadow-lg hover:scale-105',
    secondary:
      'border border-morandi-stone-300 text-morandi-stone-600 hover:bg-morandi-stone-100 focus:ring-morandi-stone-400',
    ghost:
      'text-morandi-stone-600 hover:text-morandi-stone-700 hover:bg-morandi-stone-100 focus:ring-morandi-stone-400',
    danger:
      'bg-red-600 text-white hover:bg-red-700 focus:ring-red-500 shadow-md hover:shadow-lg',
    icon: 'text-gray-600 hover:text-gray-900 hover:bg-gray-100 dark:text-gray-400 dark:hover:text-gray-100 dark:hover:bg-gray-700 focus:ring-gray-400',
    warning:
      'bg-morandi-peach-100 text-morandi-peach-700 hover:bg-morandi-peach-200 focus:ring-morandi-peach-400 border border-morandi-peach-200',
    info: 'bg-morandi-sky-200 text-morandi-sky-700 hover:bg-morandi-sky-300 focus:ring-morandi-sky-400 shadow-sm hover:shadow-md',
  }

  const sizes = {
    sm: 'text-sm px-3 py-1.5 rounded-md gap-1.5',
    md: 'text-sm px-4 py-2 rounded-lg gap-2',
    lg: 'text-base px-6 py-3 rounded-lg gap-2.5',
    'icon-sm': 'p-1 rounded',
    'icon-md': 'p-2 rounded-md',
    'icon-lg': 'p-3 rounded-lg',
  }

  return (
    <button
      ref={ref}
      className={cn(
        baseStyles,
        variants[variant],
        sizes[size],
        fullWidth && 'w-full',
        className
      )}
      disabled={disabled || loading}
      {...props}
    >
      {loading ? (
        <>
          <svg
            className="animate-spin h-4 w-4"
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
          >
            <circle
              className="opacity-25"
              cx="12"
              cy="12"
              r="10"
              stroke="currentColor"
              strokeWidth="4"
            ></circle>
            <path
              className="opacity-75"
              fill="currentColor"
              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
            ></path>
          </svg>
          <span>Loading...</span>
        </>
      ) : (
        <>
          {leftIcon && <span className="flex-shrink-0">{leftIcon}</span>}
          {children}
          {rightIcon && <span className="flex-shrink-0">{rightIcon}</span>}
        </>
      )}
    </button>
  )
}

export default Button
